import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Euro, Loader2, ShieldAlert, TrendingUp, Ban, Gift, CalendarPlus, PlayCircle, Search, FlaskConical, LayoutDashboard, Receipt, Layers, TicketPercent, Mail, Settings, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OrgActionDialog } from "@/components/OrgActionDialog";
import { PaymentsPanel } from "@/components/billing/PaymentsPanel";
import { PlansPanel } from "@/components/portal/PlansPanel";
import { PromosPanel } from "@/components/portal/PromosPanel";
import { EmailsPanel } from "@/components/portal/EmailsPanel";
import { SettingsPanel } from "@/components/portal/SettingsPanel";
import { StripePanel } from "@/components/portal/StripePanel";

const STATE = {
  active: ["Abonné", "bg-green-100 text-green-700"],
  trialing: ["Essai", "bg-sky-100 text-sky-700"],
  expired: ["Expiré", "bg-slate-200 text-slate-600"],
};
const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Kpi = ({ icon: Icon, label, value, id, tone = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-200 p-4" data-testid={id}>
    <div className="flex items-center gap-2 text-sm text-slate-500"><Icon className="h-4 w-4" /> {label}</div>
    <p className={`text-2xl font-semibold mt-1 ${tone}`}>{value}</p>
  </div>
);

const TwoFaGate = () => (
  <div className="max-w-lg rounded-lg border border-amber-300 bg-amber-50 p-6" data-testid="superadmin-2fa-required">
    <div className="flex items-center gap-2 font-semibold text-amber-900"><ShieldAlert className="h-5 w-5" /> Double authentification obligatoire</div>
    <p className="text-sm text-amber-900/80 mt-2">L'espace plateforme donne accès à tous vos clients. Activez d'abord la double authentification sur votre compte.</p>
    <Button asChild className="mt-4"><Link to="/app/security" data-testid="superadmin-goto-security">Activer la double authentification</Link></Button>
  </div>
);

const OrgRow = ({ o, onAction, onTest }) => {
  const [label, cls] = o.suspended ? ["Suspendu", "bg-red-100 text-red-700"] : STATE[o.state];
  return (
    <tr className="border-t border-slate-100 text-sm" data-testid={`sa-org-${o.org_id}`}>
      <td className="py-3 pr-3">
        <p className="font-medium">{o.name}{o.is_test && <Badge variant="outline" className="ml-2 text-[10px] border-violet-300 text-violet-700" data-testid={`sa-org-test-badge-${o.org_id}`}>Test</Badge>}</p>
        <p className="text-xs text-slate-500">{o.admins.join(", ")}{o.plan_name && <span className="text-slate-400"> · {o.plan_name}</span>}</p>
      </td>
      <td className="pr-3"><Badge className={`${cls} hover:${cls}`} data-testid={`sa-org-state-${o.org_id}`}>{label}</Badge>
        {o.payment_issue && <Badge className="ml-1 bg-red-100 text-red-700 hover:bg-red-100" data-testid={`sa-org-pastdue-${o.org_id}`}>Impayé</Badge>}
        {o.cancel_at_period_end && <Badge className="ml-1 bg-amber-100 text-amber-800 hover:bg-amber-100">Résiliation</Badge>}</td>
      <td className="pr-3 text-slate-600">{fmtDate(o.created_at)}</td>
      <td className="pr-3 text-slate-600">{o.state === "trialing" ? fmtDate(o.trial_ends_at) : o.paid_until ? fmtDate(o.paid_until) : "—"}</td>
      <td className="pr-3 text-center">{o.technicians}</td>
      <td className="pr-3 text-center">{o.machines}</td>
      <td className="pr-3 text-center" data-testid={`sa-org-licences-${o.org_id}`}>
        <span className="font-medium">{o.licences}</span>
        <span className="block text-[11px] text-slate-500">{o.state === "trialing" ? "essai" : `${o.seats_paid} payée(s)${o.seats_gift ? ` + ${o.seats_gift} offerte(s)` : ""}`}</span>
      </td>
      <td className="pr-3 text-right font-medium">{eur(o.monthly_eur)}</td>
      <td className="py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={() => onTest(o)} title={o.is_test ? "Ce n'est plus un compte de test" : "Marquer comme compte de test"} data-testid={`sa-test-${o.org_id}`} className={o.is_test ? "text-violet-600" : "text-slate-400"}><FlaskConical className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onAction("trial", o)} title="Prolonger l'essai" data-testid={`sa-trial-${o.org_id}`}><CalendarPlus className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onAction("gift", o)} title="Offrir des licences" data-testid={`sa-gift-${o.org_id}`}><Gift className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onAction("suspend", o)} title={o.suspended ? "Réactiver" : "Suspendre"} data-testid={`sa-suspend-${o.org_id}`} className={o.suspended ? "text-green-600" : "text-red-600"}>
          {o.suspended ? <PlayCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
        </Button>
      </td>
    </tr>
  );
};

const SECTIONS = [
  { id: "overview", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "clients", label: "Clients et comptes", icon: Building2 },
  { id: "stripe", label: "Compte Stripe", icon: CreditCard },
  { id: "payments", label: "Factures et paiements", icon: Receipt },
  { id: "plans", label: "Formules et prix", icon: Layers },
  { id: "promos", label: "Codes promo", icon: TicketPercent },
  { id: "emails", label: "Emails automatiques", icon: Mail },
  { id: "settings", label: "Réglages et annonce", icon: Settings },
];

const Overview = ({ ov }) => !ov ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
    <Kpi icon={Building2} label="Clients" value={ov.clients} id="sa-kpi-clients" />
    <Kpi icon={TrendingUp} label="Revenu mensuel (MRR)" value={eur(ov.mrr_eur)} id="sa-kpi-mrr" tone="text-green-700" />
    <Kpi icon={Euro} label="Chiffre d'affaires encaissé" value={eur(ov.revenue_eur)} id="sa-kpi-revenue" />
    <Kpi icon={Gift} label="Licences payées / offertes" value={`${ov.licences_paid} / ${ov.licences_gift}`} id="sa-kpi-licences" />
    <p className="sm:col-span-2 lg:col-span-4 text-sm text-slate-600" data-testid="sa-kpi-states">
      {ov.active} abonné(s) · {ov.trialing} en essai · {ov.expired} expiré(s) · {ov.suspended} suspendu(s){ov.payment_issues ? ` · ${ov.payment_issues} impayé(s)` : ""}
      {ov.test_clients ? <span className="text-slate-400"> — hors {ov.test_clients} compte(s) de test</span> : null}
    </p>
  </div>
);

const Clients = ({ orgs, onAction, onTest }) => {
  const [q, setQ] = useState("");
  const shown = (orgs || []).filter((o) => `${o.name} ${o.admins.join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 max-w-sm">
        <Search className="h-4 w-4 text-slate-400" />
        <Input placeholder="Rechercher un client ou un email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sa-search" />
      </div>
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[960px]" data-testid="sa-orgs-table">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2 px-0 pl-0 pr-3 font-medium">Client</th><th className="pr-3 font-medium">Statut</th>
              <th className="pr-3 font-medium">Inscrit le</th><th className="pr-3 font-medium">Fin essai / payé jusqu'au</th>
              <th className="pr-3 font-medium text-center">Comptes</th><th className="pr-3 font-medium text-center">Postes</th>
              <th className="pr-3 font-medium text-center">Licences</th><th className="pr-3 font-medium text-right">Mensuel</th><th />
            </tr>
          </thead>
          <tbody className="[&_td:first-child]:pl-4 [&_th:first-child]:pl-4">
            {orgs === null && <tr><td colSpan={9} className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></td></tr>}
            {orgs && !shown.length && <tr><td colSpan={9} className="py-6 text-center text-sm text-slate-500">Aucun client.</td></tr>}
            {shown.map((o) => <OrgRow key={o.org_id} o={o} onTest={onTest} onAction={onAction} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function SuperAdmin() {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [action, setAction] = useState(null);
  const [section, setSection] = useState("overview");

  const load = useCallback(() => {
    api.get("/superadmin/overview").then(({ data }) => setOv(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/superadmin/orgs").then(({ data }) => setOrgs(data)).catch(() => {});
  }, []);
  useEffect(() => { if (user?.twofa_enabled) load(); }, [user, load]);

  if (!user?.twofa_enabled) return <div className="flex-1 p-8"><TwoFaGate /></div>;
  const toggleTest = (o) => api.post(`/superadmin/orgs/${o.org_id}/test`, { is_test: !o.is_test })
    .then(() => { toast.success(o.is_test ? "Compte compté comme client réel" : "Compte marqué « Test » : il ne compte plus dans vos chiffres"); load(); })
    .catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex-1 min-h-0 flex" data-testid="superadmin-page">
      <aside className="w-72 flex-shrink-0 border-r border-slate-200 bg-[#F7F8FA]">
        <h1 className="px-6 pt-6 pb-1 text-2xl font-medium">Plateforme Netkom</h1>
        <p className="px-6 pb-4 text-xs text-slate-500">Vos clients, abonnements et emails</p>
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`sa-nav-${id}`} onClick={() => setSection(id)}
            className={`w-full flex items-center gap-3 px-6 py-3 text-[15px] transition-colors ${section === id ? "bg-slate-200/80 font-semibold" : "hover:bg-slate-100"}`}>
            <Icon className="h-5 w-5 text-slate-500" /> {label}
          </button>
        ))}
      </aside>
      <div className="flex-1 min-w-0 overflow-y-auto px-8 py-8">
        <h2 className="text-xl font-semibold mb-6">{SECTIONS.find((x) => x.id === section)?.label}</h2>
        {section === "overview" && <Overview ov={ov} />}
        {section === "clients" && <Clients orgs={orgs} onTest={toggleTest} onAction={(kind, org) => setAction({ kind, org })} />}
        {section === "stripe" && <StripePanel />}
        {section === "payments" && <PaymentsPanel />}
        {section === "plans" && <PlansPanel />}
        {section === "promos" && <PromosPanel />}
        {section === "emails" && <EmailsPanel />}
        {section === "settings" && <SettingsPanel />}
      </div>
      <OrgActionDialog action={action} onClose={() => setAction(null)} onDone={load} />
    </div>
  );
}
